import { fromJS } from "immutable";
import ACTIONS from "../constants";

// SessionStorage helpers
const NAVIGATION_STORAGE_KEY = 'navigation_state';

const loadNavigationFromStorage = () => {
  try {
    const saved = sessionStorage.getItem(NAVIGATION_STORAGE_KEY);
    if (saved) {
      const parsedData = JSON.parse(saved);
      return {
        breadcrumbHistory: parsedData.breadcrumbHistory || [],
        currentBreadcrumbIndex: parsedData.currentBreadcrumbIndex || -1,
        entityContexts: parsedData.entityContexts || {},
        currentEntity: parsedData.currentEntity || null,
      };
    }
  } catch (error) {
    console.warn('Failed to load navigation from sessionStorage:', error);
  }
  return null;
};

const saveNavigationToStorage = (navigationState) => {
  try {
    const dataToSave = {
      breadcrumbHistory: navigationState.breadcrumbHistory,
      currentBreadcrumbIndex: navigationState.currentBreadcrumbIndex,
      entityContexts: navigationState.entityContexts,
      currentEntity: navigationState.currentEntity,
    };
    sessionStorage.setItem(NAVIGATION_STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    console.warn('Failed to save navigation to sessionStorage:', error);
  }
};

// Load initial state from sessionStorage or use defaults
const savedState = loadNavigationFromStorage();
const initialState = fromJS({
  breadcrumbHistory: savedState?.breadcrumbHistory || [],
  currentBreadcrumbIndex: savedState?.currentBreadcrumbIndex || -1,
  entityContexts: savedState?.entityContexts || {},
  currentEntity: savedState?.currentEntity || null,
  isNavigating: false,
  error: "",
});

// Helper function to generate entity key
const generateEntityKey = (type, id) => `${type}-${id}`;

// Helper function to find entity index in breadcrumb history
const findEntityInHistory = (breadcrumbHistory, entityType, entityId) => {
  return breadcrumbHistory.findIndex(
    (item) => item.type === entityType && item.id === entityId
  );
};

// Helper function to build hierarchical path
const buildHierarchicalPath = (
  entityType,
  entityId,
  entityName,
  selectedTab
) => {
  const entityTypeMapping = {
    Clients: "clients",
    Portfolios: "portfolio",
    Programs: "program",
    Projects: "projects",
    Milestones: "scopeItems",
  };

  const route =
    entityType === "dashboard"
      ? "/dashboard"
      : `/${entityTypeMapping[entityType] || entityType}/${entityId}/overview`;

  return {
    id: entityType === "dashboard" ? "dashboard" : entityId,
    name: entityName,
    type: entityType,
    route: route,
    selectedTab: selectedTab,
  };
};

// Helper function to get current breadcrumb path from history
const getCurrentBreadcrumbPath = (breadcrumbHistory, currentIndex) => {
  if (currentIndex === -1 || !breadcrumbHistory.length) return [];
  return breadcrumbHistory.slice(0, currentIndex + 1);
};

export default function navigationReducer(state = initialState, action) {
  let newState;

  switch (action.type) {
    case ACTIONS.NAVIGATE_TO_ENTITY: {
      const {
        entityType,
        entityId,
        entityName,
        selectedTab,
        parentContext,
      } = action.payload;
      const entityKey = generateEntityKey(entityType, entityId);

      let newBreadcrumbHistory = state.get("breadcrumbHistory").toJS();
      let newCurrentIndex = state.get("currentBreadcrumbIndex");

      // Check if entity already exists in history
      const existingIndex = findEntityInHistory(
        newBreadcrumbHistory,
        entityType,
        entityId
      );

      if (existingIndex !== -1) {
        // If entity exists in history, navigate to that position
        newCurrentIndex = existingIndex;
        // Update the selected tab for this entity
        newBreadcrumbHistory[existingIndex].selectedTab = selectedTab;
        if (entityName) {
          newBreadcrumbHistory[existingIndex].name = entityName;
        }
      } else {
        // Add new entity to history
        const newEntity = buildHierarchicalPath(
          entityType,
          entityId,
          entityName,
          selectedTab
        );
        
        // If we're not at the end of history, truncate history from current position
        if (newCurrentIndex < newBreadcrumbHistory.length - 1) {
          newBreadcrumbHistory = newBreadcrumbHistory.slice(0, newCurrentIndex + 1);
        }
        
        // Add new entity to history
        newBreadcrumbHistory.push(newEntity);
        newCurrentIndex = newBreadcrumbHistory.length - 1;
      }

      // Update entity context
      const newEntityContexts = state.get("entityContexts").toJS();
      newEntityContexts[entityKey] = { selectedTab };

      const currentEntity = {
        type: entityType,
        id: entityId,
        name: entityName || newBreadcrumbHistory[newCurrentIndex]?.name,
      };

      newState = state
        .set("breadcrumbHistory", fromJS(newBreadcrumbHistory))
        .set("currentBreadcrumbIndex", newCurrentIndex)
        .set("entityContexts", fromJS(newEntityContexts))
        .set("currentEntity", currentEntity)
        .set("error", "");

      // Save to sessionStorage
      saveNavigationToStorage({
        breadcrumbHistory: newBreadcrumbHistory,
        currentBreadcrumbIndex: newCurrentIndex,
        entityContexts: newEntityContexts,
        currentEntity: currentEntity,
      });

      return newState;
    }

    case ACTIONS.NAVIGATE_BACK: {
      const { targetEntity } = action.payload;
      const breadcrumbHistory = state.get("breadcrumbHistory").toJS();

      // Find target entity in history
      const targetIndex = findEntityInHistory(
        breadcrumbHistory,
        targetEntity.type,
        targetEntity.id
      );

      if (targetIndex !== -1) {
        // Navigate to target position in history
        const entityKey = generateEntityKey(targetEntity.type, targetEntity.id);
        const entityContexts = state.get("entityContexts").toJS();
        const savedTab = entityContexts[entityKey]?.selectedTab;

        // Update target entity's selected tab in history
        const newBreadcrumbHistory = [...breadcrumbHistory];
        newBreadcrumbHistory[targetIndex].selectedTab = savedTab;

        newState = state
          .set("breadcrumbHistory", fromJS(newBreadcrumbHistory))
          .set("currentBreadcrumbIndex", targetIndex)
          .set("currentEntity", targetEntity)
          .set("error", "");

        // Save to sessionStorage
        saveNavigationToStorage({
          breadcrumbHistory: newBreadcrumbHistory,
          currentBreadcrumbIndex: targetIndex,
          entityContexts: entityContexts,
          currentEntity: targetEntity,
        });

        return newState;
      }

      return state;
    }

    case ACTIONS.UPDATE_ENTITY_TAB: {
      const { entityType, entityId, selectedTab } = action.payload;
      const entityKey = generateEntityKey(entityType, entityId);

      // Update entity context
      const newEntityContexts = state.get("entityContexts").toJS();
      if (!newEntityContexts[entityKey]) {
        newEntityContexts[entityKey] = {};
      }
      newEntityContexts[entityKey].selectedTab = selectedTab;

      // Update breadcrumb history if entity exists
      const newBreadcrumbHistory = state.get("breadcrumbHistory").toJS();
      const entityIndex = findEntityInHistory(
        newBreadcrumbHistory,
        entityType,
        entityId
      );

      if (entityIndex !== -1) {
        newBreadcrumbHistory[entityIndex].selectedTab = selectedTab;
      }

      newState = state
        .set("entityContexts", fromJS(newEntityContexts))
        .set("breadcrumbHistory", fromJS(newBreadcrumbHistory))
        .set("error", "");

      // Save to sessionStorage
      saveNavigationToStorage({
        breadcrumbHistory: newBreadcrumbHistory,
        currentBreadcrumbIndex: state.get("currentBreadcrumbIndex"),
        entityContexts: newEntityContexts,
        currentEntity: state.get("currentEntity"),
      });

      return newState;
    }

    case ACTIONS.SET_DIRECT_NAVIGATION: {
      const {
        entityType,
        entityId,
        entityName,
        selectedTab,
      } = action.payload;

      // For direct navigation, create minimal breadcrumb history
      const dashboardEntity = buildHierarchicalPath(
        "dashboard",
        "dashboard",
        "Dashboard"
      );
      const targetEntity = buildHierarchicalPath(
        entityType,
        entityId,
        entityName,
        selectedTab
      );

      const newBreadcrumbHistory = [dashboardEntity, targetEntity];
      const newCurrentIndex = 1; // Point to the target entity

      // Update entity context
      const entityKey = generateEntityKey(entityType, entityId);
      const newEntityContexts = state.get("entityContexts").toJS();
      newEntityContexts[entityKey] = { selectedTab };

      const currentEntity = {
        type: entityType,
        id: entityId,
        name: entityName,
      };

      newState = state
        .set("breadcrumbHistory", fromJS(newBreadcrumbHistory))
        .set("currentBreadcrumbIndex", newCurrentIndex)
        .set("entityContexts", fromJS(newEntityContexts))
        .set("currentEntity", currentEntity)
        .set("error", "");

      // Save to sessionStorage
      saveNavigationToStorage({
        breadcrumbHistory: newBreadcrumbHistory,
        currentBreadcrumbIndex: newCurrentIndex,
        entityContexts: newEntityContexts,
        currentEntity: currentEntity,
      });

      return newState;
    }

    case ACTIONS.RESET_BREADCRUMB: {
      // Reset to dashboard only
      const dashboardEntity = buildHierarchicalPath(
        "dashboard",
        "dashboard",
        "Dashboard"
      );

      const newBreadcrumbHistory = [dashboardEntity];
      const newCurrentIndex = 0;
      const currentEntity = {
        type: "dashboard",
        id: "dashboard",
        name: "Dashboard",
      };

      newState = state
        .set("breadcrumbHistory", fromJS(newBreadcrumbHistory))
        .set("currentBreadcrumbIndex", newCurrentIndex)
        .set("currentEntity", currentEntity)
        .set("error", "");

      // Save to sessionStorage
      saveNavigationToStorage({
        breadcrumbHistory: newBreadcrumbHistory,
        currentBreadcrumbIndex: newCurrentIndex,
        entityContexts: state.get("entityContexts").toJS(),
        currentEntity: currentEntity,
      });

      return newState;
    }

    case ACTIONS.ADD_TO_BREADCRUMB: {
      const {
        entityType,
        entityId,
        entityName,
        selectedTab,
      } = action.payload;

      const newEntity = buildHierarchicalPath(
        entityType,
        entityId,
        entityName,
        selectedTab
      );
      
      let newBreadcrumbHistory = state.get("breadcrumbHistory").toJS();
      let newCurrentIndex = state.get("currentBreadcrumbIndex");

      // If we're not at the end of history, truncate history from current position
      if (newCurrentIndex < newBreadcrumbHistory.length - 1) {
        newBreadcrumbHistory = newBreadcrumbHistory.slice(0, newCurrentIndex + 1);
      }
      
      // Add new entity to history
      newBreadcrumbHistory.push(newEntity);
      newCurrentIndex = newBreadcrumbHistory.length - 1;

      // Update entity context
      const entityKey = generateEntityKey(entityType, entityId);
      const newEntityContexts = state.get("entityContexts").toJS();
      newEntityContexts[entityKey] = { selectedTab };

      const currentEntity = {
        type: entityType,
        id: entityId,
        name: entityName,
      };

      newState = state
        .set("breadcrumbHistory", fromJS(newBreadcrumbHistory))
        .set("currentBreadcrumbIndex", newCurrentIndex)
        .set("entityContexts", fromJS(newEntityContexts))
        .set("currentEntity", currentEntity)
        .set("error", "");

      // Save to sessionStorage
      saveNavigationToStorage({
        breadcrumbHistory: newBreadcrumbHistory,
        currentBreadcrumbIndex: newCurrentIndex,
        entityContexts: newEntityContexts,
        currentEntity: currentEntity,
      });

      return newState;
    }

    case ACTIONS.REMOVE_FROM_BREADCRUMB: {
      const { entityType, entityId } = action.payload;
      const breadcrumbHistory = state.get("breadcrumbHistory").toJS();
      const currentIndex = state.get("currentBreadcrumbIndex");

      const entityIndex = findEntityInHistory(
        breadcrumbHistory,
        entityType,
        entityId
      );

      if (entityIndex !== -1 && entityIndex <= currentIndex) {
        // Navigate to the previous item in history
        const newCurrentIndex = entityIndex > 0 ? entityIndex - 1 : 0;
        const currentEntity = breadcrumbHistory[newCurrentIndex] || { 
          type: "dashboard", 
          id: "dashboard", 
          name: "Dashboard" 
        };

        newState = state
          .set("currentBreadcrumbIndex", newCurrentIndex)
          .set("currentEntity", currentEntity)
          .set("error", "");

        // Save to sessionStorage
        saveNavigationToStorage({
          breadcrumbHistory: breadcrumbHistory,
          currentBreadcrumbIndex: newCurrentIndex,
          entityContexts: state.get("entityContexts").toJS(),
          currentEntity: currentEntity,
        });

        return newState;
      }

      return state;
    }

    case ACTIONS.NAVIGATE_FORWARD: {
      const breadcrumbHistory = state.get("breadcrumbHistory").toJS();
      const currentIndex = state.get("currentBreadcrumbIndex");

      // Check if we can navigate forward
      if (currentIndex < breadcrumbHistory.length - 1) {
        const newIndex = currentIndex + 1;
        const targetEntity = breadcrumbHistory[newIndex];

        newState = state
          .set("currentBreadcrumbIndex", newIndex)
          .set("currentEntity", targetEntity)
          .set("error", "");

        // Save to sessionStorage
        saveNavigationToStorage({
          breadcrumbHistory: breadcrumbHistory,
          currentBreadcrumbIndex: newIndex,
          entityContexts: state.get("entityContexts").toJS(),
          currentEntity: targetEntity,
        });

        return newState;
      }

      return state;
    }

    case ACTIONS.NAVIGATE_TO_HISTORY_INDEX: {
      const { index } = action.payload;
      const breadcrumbHistory = state.get("breadcrumbHistory").toJS();

      // Check if index is valid
      if (index >= 0 && index < breadcrumbHistory.length) {
        const targetEntity = breadcrumbHistory[index];

        newState = state
          .set("currentBreadcrumbIndex", index)
          .set("currentEntity", targetEntity)
          .set("error", "");

        // Save to sessionStorage
        saveNavigationToStorage({
          breadcrumbHistory: breadcrumbHistory,
          currentBreadcrumbIndex: index,
          entityContexts: state.get("entityContexts").toJS(),
          currentEntity: targetEntity,
        });

        return newState;
      }

      return state;
    }

    case ACTIONS.SYNC_WITH_ROUTE: {
      const {
        entityType,
        entityId,
        entityName,
        selectedTab,
      } = action.payload;

      let newBreadcrumbHistory = state.get("breadcrumbHistory").toJS();
      let newCurrentIndex = state.get("currentBreadcrumbIndex");

      // Check if entity exists in history
      const existingIndex = findEntityInHistory(
        newBreadcrumbHistory,
        entityType,
        entityId
      );

      if (existingIndex !== -1) {
        // Entity exists in history, navigate to that position
        newCurrentIndex = existingIndex;
        // Update the selected tab for this entity
        newBreadcrumbHistory[existingIndex].selectedTab = selectedTab;
        if (entityName) {
          newBreadcrumbHistory[existingIndex].name = entityName;
        }
      } else {
        // Entity doesn't exist in history, add it
        const newEntity = buildHierarchicalPath(
          entityType,
          entityId,
          entityName,
          selectedTab
        );
        
        // If we're not at the end of history, truncate history from current position
        if (newCurrentIndex < newBreadcrumbHistory.length - 1) {
          newBreadcrumbHistory = newBreadcrumbHistory.slice(0, newCurrentIndex + 1);
        }
        
        // Add new entity to history
        newBreadcrumbHistory.push(newEntity);
        newCurrentIndex = newBreadcrumbHistory.length - 1;
      }

      // Update entity context
      const entityKey = generateEntityKey(entityType, entityId);
      const newEntityContexts = state.get("entityContexts").toJS();
      newEntityContexts[entityKey] = { selectedTab };

      const currentEntity = {
        type: entityType,
        id: entityId,
        name: entityName || newBreadcrumbHistory[newCurrentIndex]?.name,
      };

      newState = state
        .set("breadcrumbHistory", fromJS(newBreadcrumbHistory))
        .set("currentBreadcrumbIndex", newCurrentIndex)
        .set("entityContexts", fromJS(newEntityContexts))
        .set("currentEntity", currentEntity)
        .set("error", "");

      // Save to sessionStorage
      saveNavigationToStorage({
        breadcrumbHistory: newBreadcrumbHistory,
        currentBreadcrumbIndex: newCurrentIndex,
        entityContexts: newEntityContexts,
        currentEntity: currentEntity,
      });

      return newState;
    }

    case ACTIONS.CLEAR_NAVIGATION: {
      // Clear sessionStorage when clearing navigation
      sessionStorage.removeItem(NAVIGATION_STORAGE_KEY);
      return initialState;
    }

    default:
      return state;
  }
}
